import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { DataTable, Select, Tooltip, Typography, useSelect } from '@chilecompra/react-kit/components'
import { capitalizer, formatAmount, formatPercentage, truncate } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import { onComparatorProvidersThunk } from './ComparatorProviders.actions'
import ColumnChart from '../../components/Charts/ColumnChart'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import InfoLabel from '../../components/Labels/InfoLabel'
import ComparadorProviders from '../../components/Infographics/ComparadorProviders'
import { Wrapper, GridWrapper, Link, Info } from './ComparatorProviders.styles'

/**
 * The ComparatorProviders' container.
 */
const ComparatorProviders = ({ firstEntity, secondEntity, latestUpdate }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const inputColumnChartRef = useRef()
  const dataTableRef = useRef()
  const { endYear, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const [showTable, setShowTable] = useState(false)
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState(`Tipos_Proveedores_${years[0].value}`)
  const { data, fetching: loading } = useSelector(state => state.comparatorProviders)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(
        onComparatorProvidersThunk(
          { year: updatedValue, idEntity: [firstEntity.value, secondEntity.value] },
          distValue,
          firstEntity.name,
          secondEntity.name
        )
      )
      setFileName(`Tipos_Proveedores_${updatedValue}`)
    }
  })
  const { value: distValue, onChange: handleChangeDist } = useSelect({
    initialValue: 'amount',
    changeCallback: updatedValue => {
      dispatch(
        onComparatorProvidersThunk(
          { year: yearValue, idEntity: [firstEntity.value, secondEntity.value] },
          updatedValue,
          firstEntity.name,
          secondEntity.name
        )
      )
    }
  })
  const handleCaptureImage = () => exportToImage('ComparatorProviders', fileName)
  const handleDownloadFile = () => exportToCsv(data.csv, fileName)

  const distribution = [
    { name: 'Monto transado en pesos', value: 'amount' },
    { name: 'Órdenes compra', value: 'oc' },
    { name: 'Monto porcentual', value: 'percentage' }
  ]
  const handleShowTable = updatedValue => () => setShowTable(updatedValue)

  useEffect(() => {
    dispatch(
      onComparatorProvidersThunk(
        { year: years[0].value, idEntity: [firstEntity.value, secondEntity.value] },
        'amount',
        firstEntity.name,
        secondEntity.name
      )
    )
  }, [firstEntity, secondEntity])

  return (
    <Wrapper margin="64px auto 0">
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Tipos de proveedores
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px 0">
            Conoce cómo se distribuyen las compras realizadas por ambos organismos compradores según el tamaño de
            empresa de sus proveedores: micro, pequeña, mediana, gran empresa o sin clasificación
            <Tooltip
              content={
                <Typography color="white" fontSize="12px" lineHeight="16px">
                  Proveedores que no disponen de su clasificación por tamaño de empresa.
                </Typography>
              }
              placement="top"
            >
              <Info />
            </Tooltip>
            .
            <br />
            <br />A continuación podrás revisar la información sobre montos transados y número de órdenes de compra de
            acuerdo con el tamaño de empresa. Recuerda que estos montos que se extraen de la orden de compra que emite
            una entidad en <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a
            compromisos de los organismos, no a pagos.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
            impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={10} spacing={1} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select label="AÑO" options={years} value={yearValue} onChange={handleChangeYear} />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12}>
            <Select label="DISTRIBUCIÓN" options={distribution} value={distValue} onChange={handleChangeDist} />
          </GridWrapper>
        </GridWrapper>
        <GridWrapper item align="right" md={2} xs={12} margin="6px 0 0">
          <IconButton
            type="toggleChart"
            variant="outlined"
            singleIcon
            margin="24px 10px 0 0"
            disabled={!showTable}
            onClick={handleShowTable(false)}
          />
          <IconButton
            type="toggleTable"
            variant="outlined"
            singleIcon
            disabled={showTable}
            margin="24px 0 0"
            onClick={handleShowTable(true)}
          />
        </GridWrapper>
      </GridWrapper>
      {showTable ? (
        <GridWrapper container justifyContent="center" margin="24px 0 0">
          <GridWrapper item xs={12}>
            <DataTable
              headers={[
                { label: 'Organismo comprador', value: 0 },
                { label: 'Año', value: 1 },
                { label: 'Tipo de proveedor', value: 2 },
                { label: 'Monto total transado durante el año', value: 3 },
                { label: 'Monto porcentual', value: 5 }
              ]}
              headersCellsProps={[
                { enableSort: true },
                null,
                { enableSort: true },
                { enableSort: true },
                { enableSort: true }
              ]}
              initialRows={6}
              loading={loading}
              records={data.csv.slice(1)}
              recordsDecorators={[
                ([entity]) => truncate(capitalizer(entity), 40),
                ([, year]) => year,
                ([, , type]) => type,
                ([, , , amount]) => `$${formatAmount(amount)}`,
                ([, , , , , percentage]) => formatPercentage(percentage)
              ]}
              ref={dataTableRef}
            />
          </GridWrapper>
        </GridWrapper>
      ) : (
        <>
          <GridWrapper container justifyContent="center" margin="30px 0 0 0">
            <GridWrapper item xs={12}>
              {distValue === 'amount' ? (
                <InfoLabel label="Millones de pesos (CLP)" margin="0 0 16px" />
              ) : (
                <InfoLabel label="N° de órdenes de compra" margin="0 0 16px" />
              )}
              <ColumnChart
                categories={data.categories}
                data={data.chart}
                distValue={distValue}
                fileName={fileName}
                id="ComparatorProviders"
                inputColumnChartRef={inputColumnChartRef}
              />
            </GridWrapper>
          </GridWrapper>
          <GridWrapper container justifyContent="center">
            <GridWrapper item xs={12}>
              <InfoLabel label={firstEntity.name} color={theme.palette.charts.navyBlue} margin="0 25px 0 0" />
              <InfoLabel label={secondEntity.name} color={theme.palette.charts.darkRed} />
            </GridWrapper>
          </GridWrapper>
        </>
      )}
      <GridWrapper container justifyContent="center" margin="48px 0 0 0">
        <GridWrapper item xs={12}>
          <Infobar
            disableDownloadChart={showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<ComparadorProviders />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

ComparatorProviders.propTypes = {
  firstEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  secondEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  latestUpdate: PropTypes.string
}

export default ComparatorProviders
